import React, { useState, useEffect } from "react";
import { Icon } from "../../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider, ScaleBadge, ClasificacionBadge, ReviewBadge,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { go } from "../rutas.js";
import { TALENTION_HELPERS, clasificacionDelPuesto } from "../data.js";

import { PageHead } from "./dashboard.jsx";
import { KPIMini, ValuationStatus } from "./orgchart.jsx";
import { ValoracionMinisterio } from "./ministerio-valoracion.jsx";

/* ============================================================
   Talention — Valoración: lista y detalle
   ============================================================ */

/* ---------- LISTA ---------- */
function ValuationList() {
  const { state } = useStore();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const positions = state.positions.filter(p => {
    if (filter === "pending" && p.status === "FINALIZADA") return false;
    if (filter === "done" && p.status !== "FINALIZADA") return false;
    if (filter === "progress" && p.status !== "EN PROGRESO") return false;
    if (query && !p.name.toLowerCase().includes(query.toLowerCase()) && !p.code.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const total = state.positions.length;
  const done = state.positions.filter(p => p.status === "FINALIZADA").length;
  const inProgress = state.positions.filter(p => p.status === "EN PROGRESO").length;
  const pending = total - done - inProgress;

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Valorar Puestos"
        sub="Lista de puestos pendientes y finalizados. Inicia una valoración para asignar puntuación SVPT y escala salarial."
        actions={<Button variant="secondary" icon="download">Informe .xlsx</Button>}
      />

      <div className="grid grid-4">
        <KPIMini label="Total puestos"    value={total}      icon="briefcase"   color="#007aff" />
        <KPIMini label="Finalizadas"      value={done}       icon="circle-check" color="#34c759" />
        <KPIMini label="En progreso"      value={inProgress} icon="play"         color="#ff9f0a" />
        <KPIMini label="Pendientes"       value={pending}    icon="clock"        color="#af52de" />
      </div>

      <Card className="flush">
        <div style={{ padding: "14px 20px", display: "flex", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: "Todas" },
              { value: "pending", label: "Pendientes" },
              { value: "progress", label: "En progreso" },
              { value: "done", label: "Finalizadas" },
            ]}
          />
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginLeft: "auto",
            padding: "7px 12px", borderRadius: 999, background: "var(--bg-soft)", border: "1px solid var(--border)",
            minWidth: 240,
          }}>
            <Icon name="search" size={14} className="muted" />
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Buscar puesto..." style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, flex: 1 }} />
          </div>
        </div>
        <table className="t-table">
          <thead><tr>
            <th style={{ width: 90 }}>Código</th>
            <th>Puesto</th>
            <th>Departamento</th>
            <th>Nivel</th>
            <th>Puntos SVPT</th>
            <th>{state.metodo === "ministerio" ? "Agrupación" : "Escala"}</th>
            <th>Estado</th>
            <th style={{ width: 140 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {positions.map(p => {
              const dept = state.departments.find(d => d.id === p.departmentId);
              const lvl = state.levels.find(l => l.id === p.levelId);
              // Clasificación oficial: en Talention la escala fijada a mano si la
              // hay y, si no, la calculada; en Ministerio la agrupación. Siempre
              // solo con la valoración finalizada.
              const clasificacion = clasificacionDelPuesto(p, state);
              const v = state.valuations[p.id];
              const counts = v ? TALENTION_HELPERS.countCompletedItems(v, state.factors) : null;
              return (
                <tr key={p.id}>
                  <td><span className="row-id mono">{p.code}</span></td>
                  <td><b>{p.name}</b></td>
                  <td>{dept?.name || "—"}</td>
                  <td className="muted tiny">{lvl?.name || "—"}</td>
                  <td>
                    {p.points > 0 ? (
                      <span className="mono semi" style={{ fontSize: 14 }}>{Math.round(p.points)}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <ClasificacionBadge clasificacion={clasificacion} />
                  </td>
                  <td>
                    <div className="row" style={{ gap: 8 }}>
                      <ValuationStatus position={p} state={state} />
                      <ReviewBadge review={p.review} compact />
                    </div>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <Button size="sm" variant={p.status === "FINALIZADA" ? "secondary" : "primary"}
                        icon={p.status === "FINALIZADA" ? "edit" : "play"}
                        onClick={() => go("/valuation/" + p.id)}>
                        {p.status === "FINALIZADA" ? "Revisar" : p.status === "EN PROGRESO" ? "Continuar" : "Valorar"}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {positions.length === 0 && <tr><td colSpan="8" className="empty">No hay puestos en esta categoría</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ---------- DETALLE (FORMULARIO DE VALORACIÓN) ---------- */
function ValuationDetailTalention({ positionId }) {
  const { state, dispatch } = useStore();
  const pos = state.positions.find(p => p.id === positionId);
  const valuation = state.valuations[positionId] || {};
  const [activeGroup, setActiveGroup] = useState(state.factors.groups[0].id);
  const [showReport, setShowReport] = useState(false);
  const [showScaleOverride, setShowScaleOverride] = useState(false);

  if (!pos) {
    return (
      <Card>
        <div className="empty">
          Puesto no encontrado. <button className="btn btn-ghost btn-sm" onClick={() => go("/valuation")}>Volver</button>
        </div>
      </Card>
    );
  }

  const dept = state.departments.find(d => d.id === pos.departmentId);
  const lvl = state.levels.find(l => l.id === pos.levelId);
  const totalSVPT = state.factors.groups.reduce((s, g) => s + g.svpt, 0);

  const groupCompletion = (groupId) => {
    const items = state.factors.items[groupId].filter(i => i.selected && i.levels.length > 0);
    const done = items.filter(i => valuation[i.id]).length;
    return { total: items.length, done };
  };

  const allCompletion = TALENTION_HELPERS.countCompletedItems(valuation, state.factors);
  const progress = allCompletion.total ? Math.round(allCompletion.done / allCompletion.total * 100) : 0;
  // Dos escalas distintas y a propósito: la que saldría de los puntos de ahora mismo
  // (orientativa mientras se valora) y la oficial del puesto, que puede estar fijada a mano.
  // Sin guarda por puntos: hay una escala que cubre el 0 y findScaleForPoints ya
  // devuelve null cuando ninguna banda cubre la puntuación.
  const liveScale = TALENTION_HELPERS.findScaleForPoints(pos.points, state.scales);
  const official = TALENTION_HELPERS.resolveScaleForPosition(pos, state.scales);

  const setLevel = (itemId, levelId) => {
    dispatch({ type: "SET_VALUATION", positionId, itemId, levelId });
  };

  return (
    <div className="stack" style={{ gap: 18 }}>
      {/* Header */}
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => go("/valuation")}>
          <Icon name="chevron-left" size={14} /> Volver a la lista
        </button>
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "center" }}>
          <div>
            <div className="row" style={{ gap: 10, marginBottom: 8 }}>
              <Badge tone="brand">{pos.code}</Badge>
              <Badge>{dept?.name}</Badge>
              <Badge>{lvl?.name}</Badge>
            </div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: "-0.025em" }}>
              Valorando: {pos.name}
            </h1>
            <div className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
              {allCompletion.done} de {allCompletion.total} factores valorados · {progress}% completado
            </div>
            <div className="bar" style={{ marginTop: 14, maxWidth: 420 }}>
              <div className="bar-fill" style={{ width: progress + "%" }} />
            </div>
          </div>

          <div className="row" style={{ gap: 14, alignItems: "flex-start" }}>
            <div style={{ textAlign: "right", minWidth: 250 }}>
              <div className="card-eyebrow">Puntuación SVPT</div>
              <div className="display-num" style={{ fontSize: 44, color: official.scale ? official.scale.color : "var(--ink-400)" }}>
                {pos.points}
              </div>
              {pos.points === 0 && <div className="tiny muted">Sin puntos aún</div>}

              <div className="divider" style={{ margin: "12px 0 10px" }} />

              <div className="card-eyebrow">Escala asignada</div>
              <div style={{ marginTop: 5 }}>
                <ScaleBadge scale={official.scale} manual={official.manual} reason={official.reason} showLabel />
              </div>

              {official.manual ? (
                <div className="tiny muted" style={{ marginTop: 6, textWrap: "pretty" }}>
                  Fijada a mano{official.reason ? ` · ${official.reason}` : ""}. Por cálculo automático:{" "}
                  {liveScale ? liveScale.name : "ninguna escala"}.
                </div>
              ) : pos.status === "FINALIZADA" ? (
                official.scale ? (
                  <div className="tiny muted" style={{ marginTop: 6 }}>Asignada por puntuación</div>
                ) : (
                  <div className="tiny muted" style={{ marginTop: 6, textWrap: "pretty" }}>
                    Ninguna escala cubre los {pos.points} puntos de este puesto.
                  </div>
                )
              ) : (
                <div className="tiny muted" style={{ marginTop: 6, textWrap: "pretty" }}>
                  Previsualización: {liveScale ? <b>{liveScale.name} · {liveScale.label}</b> : "aún sin banda"}.
                  No es definitiva hasta finalizar la valoración.
                </div>
              )}

              <Button size="sm" variant="ghost" icon="pin" style={{ marginTop: 8 }}
                onClick={() => setShowScaleOverride(true)}>
                Ajustar escala
              </Button>
            </div>
          </div>
        </div>
        {totalSVPT !== 100 && (
          <div className="banner warn" style={{ borderRadius: 0, border: "none", borderTop: "1px solid #f7c97a" }}>
            <Icon name="alert" size={18} className="banner-icon" />
            <div className="tiny">
              Los pesos SVPT actuales suman <b>{totalSVPT}%</b>. La puntuación final puede no ser fiable hasta que sumen 100%.
              <button onClick={() => go("/factors")} style={{ marginLeft: 8, background: "transparent", border: "none", textDecoration: "underline", cursor: "pointer", color: "inherit", fontWeight: 600 }}>
                Ir a factores
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Split view: nav + content */}
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 18 }}>
        {/* Sidebar nav */}
        <Card style={{ padding: 12, position: "sticky", top: 76, alignSelf: "start" }}>
          <div className="card-eyebrow" style={{ padding: "4px 8px 8px" }}>Factores</div>
          <div className="stack" style={{ gap: 4 }}>
            {state.factors.groups.map(g => {
              const c = groupCompletion(g.id);
              const isActive = activeGroup === g.id;
              const completed = c.done === c.total && c.total > 0;
              return (
                <button key={g.id}
                  onClick={() => setActiveGroup(g.id)}
                  style={{
                    border: "none", padding: "10px 12px", borderRadius: 10,
                    background: isActive ? "var(--bg-soft)" : "transparent",
                    cursor: "pointer", textAlign: "left",
                    display: "flex", gap: 10, alignItems: "center", width: "100%",
                    borderLeft: `3px solid ${isActive ? g.color : "transparent"}`,
                  }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: g.iconBg, color: g.color,
                    display: "grid", placeItems: "center",
                  }}>{completed ? <Icon name="check" size={14} /> : <Icon name={g.icon} size={14} />}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="semi" style={{ fontSize: 13 }}>{g.short}</div>
                    <div className="tiny muted">{c.done}/{c.total} · SVPT {g.svpt}%</div>
                  </div>
                  {isActive && <Icon name="chevron-right" size={14} className="muted" />}
                </button>
              );
            })}
          </div>

          <div className="divider" style={{ margin: "10px 0" }} />

          <div style={{ padding: "0 8px 4px" }}>
            <div className="card-eyebrow">Progreso</div>
            <div className="row" style={{ gap: 10, marginTop: 8 }}>
              <Donut value={progress} size={60} stroke={6} color="var(--brand-500)">
                <span className="bold" style={{ fontSize: 13 }}>{progress}%</span>
              </Donut>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="tiny muted">Completado</div>
                <div className="semi" style={{ fontSize: 13.5 }}>{allCompletion.done}/{allCompletion.total}</div>
              </div>
            </div>
            {progress === 100 && (
              <Button variant="primary" style={{ width: "100%", marginTop: 14 }} icon="check"
                onClick={() => setShowReport(true)}>
                Ver informe
              </Button>
            )}
          </div>
        </Card>

        {/* Active group content */}
        <ValuationGroup
          key={activeGroup}
          group={state.factors.groups.find(g => g.id === activeGroup)}
          items={state.factors.items[activeGroup].filter(i => i.selected && i.levels.length > 0)}
          valuation={valuation}
          onSet={setLevel}
          allGroups={state.factors.groups}
          onNext={() => {
            const idx = state.factors.groups.findIndex(g => g.id === activeGroup);
            if (idx < state.factors.groups.length - 1) setActiveGroup(state.factors.groups[idx + 1].id);
            else setShowReport(true);
          }}
          onPrev={() => {
            const idx = state.factors.groups.findIndex(g => g.id === activeGroup);
            if (idx > 0) setActiveGroup(state.factors.groups[idx - 1].id);
          }}
        />
      </div>

      <ValuationReport
        open={showReport}
        onClose={() => setShowReport(false)}
        position={pos}
        valuation={valuation}
        state={state}
      />

      <ScaleOverrideModal
        open={showScaleOverride}
        onClose={() => setShowScaleOverride(false)}
        position={pos}
        scales={state.scales}
        autoScale={liveScale}
        onSave={(scaleId, reason) => {
          dispatch({ type: "SET_POSITION_SCALE_OVERRIDE", positionId: pos.id, scaleId, reason });
          setShowScaleOverride(false);
        }}
        onClear={() => {
          dispatch({ type: "SET_POSITION_SCALE_OVERRIDE", positionId: pos.id, scaleId: null });
          setShowScaleOverride(false);
        }}
      />
    </div>
  );
}

/* ---------- EXCEPCIÓN: ESCALA FIJADA A MANO ---------- */
function ScaleOverrideModal({ open, onClose, position, scales, autoScale, onSave, onClear }) {
  const sorted = [...scales].sort((a, b) => b.min - a.min);
  const [scaleId, setScaleId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    // Sin caer en la primera escala de la lista (la más alta), que no tiene nada
    // que ver con el puesto: mejor sin elegir que preseleccionar algo equivocado.
    const initial = position?.scaleOverrideId ?? autoScale?.id;
    setScaleId(initial != null ? String(initial) : "");
    setReason(position?.scaleOverrideReason || "");
    // Se depende del id y no del objeto: el store rehace los puestos en cada acción
    // y no queremos borrar el motivo a medio escribir.
  }, [open, position?.id]);

  if (!position) return null;

  const chosen = sorted.find(s => String(s.id) === scaleId) || null;
  const hasOverride = position.scaleOverrideId != null;
  const canSave = chosen !== null && reason.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Ajustar escala del puesto" width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          {hasOverride && (
            <Button variant="secondary" icon="swap" onClick={onClear}>Volver al cálculo automático</Button>
          )}
          <Button variant="primary" icon="pin" style={{ opacity: canSave ? 1 : 0.5 }}
            onClick={() => canSave && onSave(chosen.id, reason.trim())}>
            Fijar escala
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="banner info">
          <Icon name="info" size={16} className="banner-icon" />
          <div className="tiny" style={{ flex: 1 }}>
            Fijar la escala a mano <b>no cambia los puntos SVPT</b> del puesto ({position.points} pts): solo
            sustituye la banda asignada. La excepción queda marcada como tal y no se recalcula aunque cambien
            los puntos o los rangos de las escalas.
          </div>
        </div>

        <div className="spread" style={{
          gap: 10, padding: "10px 14px", borderRadius: 12,
          background: "var(--bg-soft)", border: "1px solid var(--border)",
        }}>
          <div>
            <div className="card-eyebrow">Por cálculo automático</div>
            <div className="semi" style={{ fontSize: 13.5, marginTop: 2 }}>
              {autoScale ? `${autoScale.name} · ${autoScale.label}` : "Sin banda para esta puntuación"}
            </div>
          </div>
          <Badge tone={hasOverride ? "warn" : "good"}>
            {hasOverride ? "Escala fijada a mano" : "Asignación automática"}
          </Badge>
        </div>

        <Field label="Escala">
          <select className="select" value={scaleId} onChange={e => setScaleId(e.target.value)}>
            {scaleId === "" && <option value="">— Elige una escala —</option>}
            {sorted.map(s => (
              <option key={s.id} value={s.id}>{s.name} · {s.label} · {s.min} – {s.max} pts</option>
            ))}
          </select>
        </Field>

        <Field label="Motivo de la excepción" hint="Obligatorio: se guarda junto al puesto y aparece en el informe de valoración.">
          <textarea className="textarea" rows={3} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Ej. Acuerdo de convenio: el puesto conserva su banda hasta la próxima revisión." />
        </Field>

        {!canSave && (
          <div className="tiny muted">
            {!chosen ? "Elige una escala para poder fijarla." : "Indica un motivo para poder fijar la escala."}
          </div>
        )}
      </div>
    </Modal>
  );
}

function ValuationGroup({ group, items, valuation, onSet, onNext, onPrev, allGroups }) {
  const completedCount = items.filter(i => valuation[i.id]).length;
  const idx = allGroups.findIndex(g => g.id === group.id);
  const isLast = idx === allGroups.length - 1;
  const isFirst = idx === 0;

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card style={{ borderTop: `4px solid ${group.color}`, paddingTop: 18 }}>
        <div className="row" style={{ gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: group.iconBg, color: group.color,
            display: "grid", placeItems: "center",
          }}><Icon name={group.icon} size={22} /></div>
          <div style={{ flex: 1 }}>
            <div className="card-eyebrow">Factor {idx + 1} de {allGroups.length} · SVPT {group.svpt}%</div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.022em", marginTop: 2 }}>{group.name}</h2>
            <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>{group.description}</div>
          </div>
          <Badge tone={completedCount === items.length ? "good" : ""}>{completedCount} / {items.length}</Badge>
        </div>
      </Card>

      {items.map((item, i) => (
        <ValuationItem
          key={item.id}
          item={item}
          chosenLevel={valuation[item.id]}
          onChoose={(lvlId) => onSet(item.id, lvlId)}
          accent={group.color}
          index={i + 1}
        />
      ))}

      <div className="spread" style={{ marginTop: 8 }}>
        <Button variant="ghost" icon="chevron-left" onClick={onPrev}
          style={{ visibility: isFirst ? "hidden" : "visible" }}>
          Anterior
        </Button>
        <Button variant="primary" onClick={onNext}>
          {isLast ? "Ver informe" : "Siguiente factor"} <Icon name="arrow-right" size={13} />
        </Button>
      </div>
    </div>
  );
}

function ValuationItem({ item, chosenLevel, onChoose, accent, index }) {
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "18px 22px 14px", borderBottom: chosenLevel ? "1px solid var(--border)" : "none" }}>
        <div className="row" style={{ gap: 10 }}>
          <span style={{
            width: 22, height: 22, borderRadius: 999,
            background: chosenLevel ? accent : "var(--ink-150)", color: chosenLevel ? "white" : "var(--ink-500)",
            display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700,
          }}>{chosenLevel ? <Icon name="check" size={12} /> : index}</span>
          <div style={{ flex: 1 }}>
            <div className="semi" style={{ fontSize: 15, letterSpacing: "-0.01em" }}>{item.name}</div>
            {item.desc && <div className="muted" style={{ fontSize: 12.5, marginTop: 4, textWrap: "pretty" }}>{item.desc}</div>}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <Badge>Peso {item.weight}</Badge>
            {item.gender !== "NEUTRO" && (
              <Badge style={{ background: item.gender === "MASCULINO" ? "#dfeeff" : "#fde2e8", color: item.gender === "MASCULINO" ? "#0a52c2" : "#a01755" }}>
                {item.gender}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div className="stack" style={{ gap: 4 }}>
          {item.levels.map(lvl => {
            const isOn = chosenLevel === lvl.id;
            return (
              <button key={lvl.id} onClick={() => onChoose(lvl.id)}
                style={{
                  width: "100%", textAlign: "left", border: "none",
                  background: isOn ? accent + "0f" : "transparent",
                  padding: "10px 14px", borderRadius: 10,
                  display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer",
                  transition: "background 100ms ease",
                }}
                onMouseEnter={e => { if (!isOn) e.currentTarget.style.background = "var(--bg-soft)"; }}
                onMouseLeave={e => { if (!isOn) e.currentTarget.style.background = "transparent"; }}
              >
                <span className={"radio " + (isOn ? "on" : "")}
                  style={isOn ? { borderColor: accent } : {}} />
                <div style={{ flex: 1 }}>
                  <span className="semi" style={{ fontSize: 13.5 }}>{lvl.label}</span>
                  {lvl.desc && <span className="muted" style={{ fontSize: 12.5, marginLeft: 8 }}>· {lvl.desc}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ---------- INFORME FINAL ---------- */
function ValuationReport({ open, onClose, position, valuation, state }) {
  if (!position) return null;

  // El informe nunca puede ocultar una excepción: enseña la escala oficial y,
  // si está fijada a mano, también la que habría salido del cálculo.
  const { scale, manual, reason } = TALENTION_HELPERS.resolveScaleForPosition(position, state.scales);
  const autoScale = TALENTION_HELPERS.findScaleForPoints(position.points, state.scales);

  // breakdown by group
  const breakdown = state.factors.groups.map(g => {
    const items = state.factors.items[g.id].filter(i => i.selected && i.levels.length > 0);
    const weightSum = items.reduce((s, i) => s + (i.weight || 0), 0) || 1;
    let groupScore = 0;
    items.forEach(it => {
      const chosen = valuation[it.id];
      if (!chosen) return;
      const idx = it.levels.findIndex(l => l.id === chosen);
      if (idx < 0) return;
      const max = it.levels.length - 1;
      const ratio = max === 0 ? 0 : idx / max;
      groupScore += (it.weight / weightSum) * ratio;
    });
    return {
      ...g,
      score: Math.round((g.svpt / 100) * groupScore * 1000),
      items: items.map(it => {
        const chosen = valuation[it.id];
        const lvl = chosen ? it.levels.find(l => l.id === chosen) : null;
        return { ...it, chosenLevel: lvl };
      }),
    };
  });

  const dept = state.departments.find(d => d.id === position.departmentId);
  const lvl = state.levels.find(l => l.id === position.levelId);

  return (
    <Modal open={open} onClose={onClose} title="Informe de Valoración" width={760}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          <Button variant="secondary" icon="download">Descargar PDF</Button>
          <Button variant="primary" icon="check" onClick={onClose}>Finalizar valoración</Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 18 }}>
        <div style={{
          padding: 24, borderRadius: 16,
          background: scale ? `linear-gradient(135deg, ${scale.color}, ${scale.color}cc)` : "linear-gradient(135deg, var(--ink-700), var(--ink-900))",
          color: "white",
        }}>
          <div className="row" style={{ gap: 8, marginBottom: 6, opacity: 0.85 }}>
            <Badge style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>{position.code}</Badge>
            <Badge style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>{dept?.name}</Badge>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>{position.name}</div>
          <div className="row" style={{ gap: 20, marginTop: 16 }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.8, textTransform: "uppercase", letterSpacing: 0.06 + "em", fontWeight: 600 }}>Puntos SVPT</div>
              <div style={{ fontSize: 44, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1 }}>{position.points}</div>
            </div>
            <div style={{ width: 1, background: "rgba(255,255,255,0.25)" }} />
            <div>
              <div style={{ fontSize: 11, opacity: 0.8, textTransform: "uppercase", letterSpacing: 0.06 + "em", fontWeight: 600 }}>Escala</div>
              <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{scale ? `${scale.name} · ${scale.label}` : "Sin escala asignada"}</div>
              {scale && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>Rango {scale.min} – {scale.max}</div>}
              {manual && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 5, marginTop: 7,
                  padding: "2px 9px", borderRadius: 999,
                  background: "rgba(255,255,255,0.22)", fontSize: 11, fontWeight: 650,
                }}>
                  <Icon name="pin" size={11} /> Fijada a mano
                </div>
              )}
            </div>
            <div style={{ width: 1, background: "rgba(255,255,255,0.25)" }} />
            <div>
              <div style={{ fontSize: 11, opacity: 0.8, textTransform: "uppercase", letterSpacing: 0.06 + "em", fontWeight: 600 }}>Nivel jerárquico</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{lvl?.name || "—"}</div>
            </div>
          </div>
        </div>

        {manual && (
          <div className="banner warn">
            <Icon name="alert" size={16} className="banner-icon" />
            <div className="tiny" style={{ flex: 1 }}>
              <b>Escala fijada a mano como excepción.</b> No procede del cálculo automático por puntuación.
              <div style={{ marginTop: 4 }}>
                Motivo: {reason ? reason : <i>sin motivo registrado</i>}
              </div>
              <div style={{ marginTop: 2 }}>
                Por cálculo automático ({position.points} pts) le correspondería{" "}
                <b>{autoScale ? `${autoScale.name} · ${autoScale.label}` : "ninguna escala"}</b>
                {position.status !== "FINALIZADA" && " (valoración sin finalizar)"}.
              </div>
            </div>
          </div>
        )}

        <Card title="Desglose por factor" style={{ padding: 0 }}>
          <div>
            {breakdown.map(b => (
              <div key={b.id} style={{ padding: 18, borderBottom: "1px solid var(--border)" }}>
                <div className="spread" style={{ marginBottom: 10 }}>
                  <div className="row" style={{ gap: 10 }}>
                    <span style={{
                      width: 24, height: 24, borderRadius: 6,
                      background: b.iconBg, color: b.color,
                      display: "inline-grid", placeItems: "center",
                    }}><Icon name={b.icon} size={13} /></span>
                    <div>
                      <div className="semi" style={{ fontSize: 14 }}>{b.name}</div>
                      <div className="tiny muted">SVPT {b.svpt}% · {b.items.length} factores</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: b.color, letterSpacing: "-0.02em" }}>{b.score}</div>
                </div>
                <div className="bar" style={{ marginBottom: 12 }}>
                  <div className="bar-fill" style={{ width: ((b.score / (b.svpt * 10 || 1)) * 100) + "%", background: b.color }} />
                </div>
                <div className="grid grid-2" style={{ gap: 8 }}>
                  {b.items.map(it => (
                    <div key={it.id} className="row" style={{ gap: 10, padding: "8px 12px", background: "var(--bg-soft)", borderRadius: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="tiny muted">{it.name}</div>
                        <div className="semi" style={{ fontSize: 13 }}>{it.chosenLevel?.label || <span className="muted">—</span>}</div>
                      </div>
                      <Badge>{it.weight}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Modal>
  );
}

/* Detalle de valoración: la pantalla del método de la empresa. Son dos
   componentes para que cada uno tenga sus propios hooks. */
function ValuationDetail({ positionId }) {
  const { state } = useStore();
  return state.metodo === "ministerio"
    ? <ValoracionMinisterio positionId={positionId} />
    : <ValuationDetailTalention positionId={positionId} />;
}

export { ValuationList, ValuationDetail };